import React, { useEffect, useState } from "react";
import { Avatar, Box, Link, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import color from "../../../Constants/colors";
import { useWorkspaceColors } from "../../../Contexts/WorkspaceColorContext";
import {
  useOwnVoicesProfile,
  useUpdateOwnVoicesProfile,
} from "../../../Query/useVoicesProfiles";
import Loading from "../../Common/Loading";
import { ComoButton } from "../../Common/ComoButton";
import VoicesCard from "../Common/VoicesCard";
import VoicesField from "../Common/VoicesField";
import VoicesInput from "../Common/VoicesInput";
import VoicesTermChips from "../Common/VoicesTermChips";

const VoicesMyProfile: React.FC = () => {
  const { t } = useTranslation("voicesProfiles");
  const { colors: workspace } = useWorkspaceColors();
  const { data: person, isLoading, isError } = useOwnVoicesProfile();
  const updateProfile = useUpdateOwnVoicesProfile();
  const [preferences, setPreferences] = useState("");

  useEffect(() => {
    if (person?.profile) setPreferences(person.profile.preferences);
  }, [person]);

  if (isLoading) {
    return (
      <Loading
        backgroundColor={color.gray50}
        color1={workspace.accent}
        color2={color.gray900}
      />
    );
  }

  if (isError || !person?.profile) {
    return (
      <VoicesCard title={t("myProfile")}>
        <Typography variant="body2" sx={{ color: color.gray500 }}>
          {t("noOwnProfile")}
        </Typography>
      </VoicesCard>
    );
  }

  const profile = person.profile;

  return (
    <>
      <VoicesCard
        title={t("myProfile")}
        subtitle={t("myProfileSubtitle")}
        action={
          <ComoButton
            onClick={() => updateProfile.mutate(preferences)}
            disabled={
              updateProfile.isPending || preferences === profile.preferences
            }
          >
            {t("save")}
          </ComoButton>
        }
      >
        <Stack spacing={3}>
          <VoicesField label={t("preferences")} hint={t("preferencesHint")}>
            <VoicesInput
              multiline
              minRows={6}
              placeholder={t("preferencesPlaceholder")}
              value={preferences}
              onChange={(e) => setPreferences(e.target.value)}
            />
          </VoicesField>

          <VoicesField label={t("blockedTerms")} hint={t("blockedTermsOwnHint")}>
            <VoicesTermChips
              terms={profile.blocked_terms}
              emptyText={t("noBlockedTerms")}
            />
          </VoicesField>

          <Box sx={{ display: "flex", gap: 4 }}>
            <VoicesField label={t("weeklyLimit")}>
              <Typography variant="body2" sx={{ color: color.gray700 }}>
                {profile.weekly_limit ?? t("noLimit")}
              </Typography>
            </VoicesField>
            <VoicesField label={t("bufferChannel")}>
              {person.buffer_channel ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Avatar
                    src={person.buffer_channel.avatar ?? undefined}
                    sx={{ width: 24, height: 24 }}
                  />
                  <Typography variant="body2" sx={{ color: color.gray900 }}>
                    {person.buffer_channel.display_name ??
                      person.buffer_channel.name}
                  </Typography>
                  {person.buffer_channel.external_link && (
                    <Link
                      href={person.buffer_channel.external_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      sx={{ color: workspace.accent }}
                    >
                      {t("openOnLinkedIn")}
                    </Link>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: color.gray700 }}>
                  {profile.buffer_channel_id
                    ? t("bufferConnected")
                    : t("bufferNotConnected")}
                </Typography>
              )}
            </VoicesField>
          </Box>
        </Stack>
      </VoicesCard>

      <VoicesCard title={t("previousPosts")} subtitle={t("previousPostsSubtitle")}>
        <Typography variant="body2" sx={{ color: color.gray500 }}>
          {t("previousPostsEmpty")}
        </Typography>
      </VoicesCard>
    </>
  );
};

export default VoicesMyProfile;
